import { ErrorMessage, ValueOf } from '../../types/main';
import * as ReportActions from '../actions/reports';

export const MARKET_REPORT_REQUEST = 'MARKET_REPORT_REQUEST';
export const MARKET_REPORT_REQUEST_SUCCESS = 'MARKET_REPORT_REQUEST_SUCCESS';
export const MARKET_REPORT_REQUEST_FAILED = 'MARKET_REPORT_REQUEST_FAILED';

export const MARKET_REPORT_EXPORT_REQUEST = 'MARKET_REPORT_EXPORT_REQUEST';
export const MARKET_REPORT_EXPORT_REQUEST_SUCCESS = 'MARKET_REPORT_EXPORT_REQUEST_SUCCESS';
export const MARKET_REPORT_EXPORT_REQUEST_FAILED = 'MARKET_REPORT_EXPORT_REQUEST_FAILED';

export const MONTHLY_REPORT_REQUEST = 'MONTHLY_REPORT_REQUEST';
export const MONTHLY_REPORT_REQUEST_SUCCESS = 'MONTHLY_REPORT_REQUEST_SUCCESS';
export const MONTHLY_REPORT_REQUEST_FAILED = 'MONTHLY_REPORT_REQUEST_FAILED';

export type ReportsActionsTypes = ReturnType<ValueOf<typeof ReportActions>>;

export type MarketReportExportRequestActionType =
  | ReturnType<typeof ReportActions.marketReportExportRequest>;

export interface MarketReportsRequestSuccess {
  marketReport: any[]
  total: number
}

export interface ReportsState {
  marketReport: any[];
  total: number;
  loading: boolean;
  error: ErrorMessage['message'];
  monthlyReport: MonthlyReport | null;
  monthlyReportLoading: boolean;
  monthlyReportError: ErrorMessage['message'];
}

export interface MarketReport {
  lotNumber: string;
  communityName: string;
  daysBeforeSale: number;
  marketingName: string;
  propertyId: number;
  saleDate: Date;
  address: string;
  visits: number;
}

export type MonthlyReportRequestPayload = {
  builderIds: string;
  enterpriseId?: number | null;
  startDate: string;
  endDate: string;
  communityIds?: string | null;
};

export type MonthlyReportRequestActionType =
  ReturnType<typeof ReportActions.monthlyReportRequest>;

export interface MarketReportsRequestSuccess {
  marketReport: any[];
  total: number;
}

export interface MonthlyReportMeta {
  generatedBy: string;
  generatedAt: string;
  timezone: string;
  startDate: string;
  endDate: string;
  title: string;
  client: string;
}

export interface MonthlyReportSummary {
  totalTours: number;
  completed: number;
  missed: number;
  cancelled: number;
  outsideOfficeHoursTours: number;
  outsideOfficeHoursPercentage: number;
}

export interface MonthlyReportBySource {
  onSiteAndSchedulingSite: number;
  liveSupport: number;
  onDemandTours: number;
}

export interface MonthlyReportByVisitorType {
  visitor: number;
  agent: number;
  client: number;
  contractor: number;
}

export interface MonthlyReportByHomeType {
  inventory: number;
  model: number;
}

export interface MonthlyReportToursPerVisitor {
  average: number;
  oneTour: number;
  twoTours: number;
  threeTours: number;
  fourTours: number;
  fivePlusTours: number;
}

export interface MonthlyReportToursByTime {
  morning: number;
  midday: number;
  afternoon: number;
  evening: number;
}

export interface MonthlyReportToursByDay {
  sunday: number;
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
}

export interface MonthlyReportHeatmapCell {
  day: string;
  hourLabel: string;
  value: number;
}

export interface MonthlyReportCommunityStatus {
  community: string;
  all: number;
  completed: number;
  missed: number;
  cancelled: number;
}

export interface MonthlyReportCompletedByCommunity {
  community: string;
  completed: number;
}

export interface MonthlyReport {
  meta: MonthlyReportMeta;
  summary: MonthlyReportSummary;
  bySource: MonthlyReportBySource;
  byVisitorType: MonthlyReportByVisitorType;
  byHomeType: MonthlyReportByHomeType;
  toursPerVisitor: MonthlyReportToursPerVisitor;
  toursByTime: MonthlyReportToursByTime;
  toursByDay: MonthlyReportToursByDay;
  topAllToursHeatMap: MonthlyReportHeatmapCell[];
  topCommunities: MonthlyReportCommunityStatus[];
  completedByCommunity: MonthlyReportCompletedByCommunity[];
}

export interface OpportunityReportSummary {
  builderName: string;
  states: string[];
  feedCommunities: number;
  activeCommunities: number;
  communityOpportunities: number;
  feedSpecHomes: number;
  activeProperties: number;
  propertyOpportunities: number;
  needsReviewCommunities: number;
  needsReviewProperties: number;
}

export type OpportunityPropertyStatus =
  | 'NEEDS_REVIEW'
  | 'MISSING_PROPERTY'
  | 'UPDATE_PROPERTY'
  | 'COVERED';

export interface OpportunityCommunityRow {
  externalCommunityId: string | null;
  communityName: string;
  city: string;
  state: string;
  zip: string;
  feedSpecHomesCount: number;
  activePropertyCount: number;
  matchedActiveCommunityId: number | null;
}

export interface OpportunityPropertyRow {
  externalIdentifier: string | null;
  externalCommunityId: string | null;
  communityName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  matchedActivePropertyId: number | null;
  status: OpportunityPropertyStatus;
}

export interface OpportunityReportData {
  summary: OpportunityReportSummary;
  communities: OpportunityCommunityRow[];
  properties: OpportunityPropertyRow[];
}
